/* eslint-disable no-process-env */

import {
  IClientOpts,
  LOCAL_GRAPHQL_ENDPOINT,
  LOCAL_SEEDRS_API_ENDPOINT,
} from '@r-client/shared/data/client';
import { IPublicConfig, TAuthPersistence } from '@r-client/shared/util/core';
import type { TDeployEnvironment } from '@r-client/wallet/feature/connect';

export interface IServerConfig {
  rollbarServerToken: string | undefined;
  skipBuildStaticGeneration: boolean;
  rwalletPartnerKeys: {
    INX: string;
    TEST_ONLY_PARTNER: string;
  };
  environment: TDeployEnvironment;
}

// NEXT_PUBLIC_APP_ENV is the only signal that separates staging from production; NODE_ENV
// cannot, because Next sets it to 'production' for every built deployment. An unrecognised
// value in a built deployment resolves to 'production' so that a security gate fails closed.
const readDeployEnvironment = (): TDeployEnvironment => {
  const declared = process.env.NEXT_PUBLIC_APP_ENV;

  if (declared === 'production' || declared === 'staging') return declared;

  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
};

export const getServerConfig = (): IServerConfig => {
  return {
    rollbarServerToken: process.env.ROLLBAR_SERVER_TOKEN,
    skipBuildStaticGeneration:
      process.env.SKIP_BUILD_STATIC_GENERATION === 'true',
    rwalletPartnerKeys: {
      INX: process.env.INX_PUBLIC_KEY || 'INX_KEY_WAS_NOT_SET',
      TEST_ONLY_PARTNER:
        process.env.RWALLET_TEST_PRIVATE_KEY || 'TEST_KEY_WAS_NOT_SET',
    },
    environment: readDeployEnvironment(),
  };
};

const googleMapsEmbedUrl =
  process.env.NEXT_PUBLIC_GOOGLE_MAP_BASE_URL &&
  process.env.NEXT_PUBLIC_GOOGLE_MAP_EMBED_KEY
    ? process.env.NEXT_PUBLIC_GOOGLE_MAP_BASE_URL.replace(
        '<<embed_key>>',
        process.env.NEXT_PUBLIC_GOOGLE_MAP_EMBED_KEY
      )
    : undefined;

/**
 * Clamps the Datadog RUM sample rate to 0-100, defaulting to 0 ("collect
 * nothing"). A missing or malformed value should cost us data, never money.
 */
const parseSampleRate = (raw: string | undefined): number => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : 0;
};

export const getPublicConfig = (): IPublicConfig => {
  if (process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT === undefined) {
    throw new Error(
      'process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT not found. Please provide NEXT_PUBLIC_GRAPHQL_ENDPOINT in env.'
    );
  }

  if (process.env.NEXT_PUBLIC_URL_ORIGIN === undefined) {
    throw new Error(
      'process.env.NEXT_PUBLIC_URL_ORIGIN not found. Please provide NEXT_PUBLIC_URL_ORIGIN in env.'
    );
  }

  if (process.env.NEXT_PUBLIC_SEEDRS_API_BASE_URL === undefined) {
    throw new Error(
      'process.env.NEXT_PUBLIC_SEEDRS_API_BASE_URL not found. Please provide NEXT_PUBLIC_SEEDRS_API_BASE_URL in env.'
    );
  }

  // TODO: MK Uncomment this when we have bifrost as default
  // if (process.env.NEXT_PUBLIC_GLOBAL_AUTH_BASE_URL === undefined) {
  //   throw new Error(
  //     'process.env.NEXT_PUBLIC_GLOBAL_AUTH_BASE_URL not found. Please provide NEXT_PUBLIC_GLOBAL_AUTH_BASE_URL in env.'
  //   );
  // }

  return {
    publicTld: process.env.NEXT_PUBLIC_TLD,
    graphqlEndpointUrl:
      process.env.NODE_ENV === 'development' && typeof window === 'object'
        ? LOCAL_GRAPHQL_ENDPOINT
        : process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT,
    seedrsApiBaseUrl:
      process.env.NODE_ENV === 'development' &&
      typeof window === 'object' &&
      !process.env.NEXT_PUBLIC_SEEDRS_API_BASE_URL.includes('republic.local')
        ? LOCAL_SEEDRS_API_ENDPOINT
        : process.env.NEXT_PUBLIC_SEEDRS_API_BASE_URL,
    globalAuthBaseUrl: process.env.NEXT_PUBLIC_GLOBAL_AUTH_BASE_URL
      ? '/api/global-auth'
      : undefined,
    republicEuropeBaseUrl: process.env.NEXT_PUBLIC_REPUBLIC_EUROPE_BASE_URL,
    applicationEnvironment:
      (process.env.NEXT_PUBLIC_APP_ENV as 'staging') || 'production',
    applicationVersion: process.env.NEXT_PUBLIC_APP_VERSION || 'no-version-set',
    rollbarClientToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    googleRecaptchaSiteKey: process.env.NEXT_PUBLIC_GRECAPTCHA_V3_SITE_KEY,
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    segmentClientWriteKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    googleMapsEmbedUrl,
    zendeskKey: process.env.NEXT_PUBLIC_ZENDESK_KEY,
    applicationURLOrigin: process.env.NEXT_PUBLIC_URL_ORIGIN,
    authPersistence: {
      type: 'cookie',
      cookiePrefix: process.env.NEXT_PUBLIC_AUTH_COOKIE_PREFIX,
    } as TAuthPersistence,
    brazeApiKey: process.env.NEXT_PUBLIC_BRAZE_API_KEY,
    brazeBaseUrl: process.env.NEXT_PUBLIC_BRAZE_BASE_URL,
    froalaKey: process.env.NEXT_PUBLIC_FROALA_KEY,
    wallabyApiKey: process.env.NEXT_PUBLIC_WALLABY_API_KEY,
    wallabyBaseUrl: process.env.NEXT_PUBLIC_WALLABY_BASE_URL,
    comingSoonTokensList: process.env.NEXT_PUBLIC_COMING_SOON_TOKENS_LIST,
    rumApplicationId: process.env.NEXT_PUBLIC_RUM_APPLICATION_ID,
    rumClientToken: process.env.NEXT_PUBLIC_RUM_CLIENT_TOKEN,
    rumEnabled: process.env.NEXT_PUBLIC_RUM_ENABLED === 'true',
    rumSessionSampleRate: parseSampleRate(
      process.env.NEXT_PUBLIC_RUM_SAMPLE_RATE
    ),
    unifyApiKey: process.env.NEXT_PUBLIC_UNIFY_DATA_API_KEY,
    cloudflareTurnstileSiteKey:
      process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY,
  };
};

export const apiClientConfig: IClientOpts = {
  apiAuthHeader: process.env.NEXT_PUBLIC_GRAPHQL_AUTH,
  apiUri: getPublicConfig().graphqlEndpointUrl,
  // Keep the client name stable for analytics and API identification.
  clientName: 'republic-web',
  queryBatchingEnabled: !process.env.NEXT_PUBLIC_DISABLE_QUERY_BATCHING,
  clientVersion: process.env.NEXT_PUBLIC_APP_VERSION || 'no-version',
  authPersistence: getPublicConfig().authPersistence,
  connectToDevTools: process.env.NODE_ENV !== 'production',
};
